import { useRef } from 'react';
import "@/App.css";
import { AppProvider } from './context/AppContext';
import {
  Header,
  HeroSection,
  CalculatorInputs,
  ResultsDisplay,
  BreakdownTable,
  StrategyComparison,
} from './components/calculator';

import { lazy, Suspense } from "react";

const InvestmentChart = lazy(() => import('./components/calculator/InvestmentChart'));
import { Toaster } from './components/ui/sonner';

const CalculatorApp = () => {
  const calculatorRef = useRef(null);
  
  const scrollToCalculator = () => {
    calculatorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <Header />
      
      {/* Hero Section */}
      <HeroSection onScrollToCalculator={scrollToCalculator} />
      {/* SEO Content Section */}
<section className="max-w-4xl mx-auto px-4 py-12 text-muted-foreground">
  <h1 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
    Calculator Dobândă Compusă – Simulează Creșterea Economiilor Tale
  </h1>

  <p className="mb-4">
    Descoperă cum îți pot crește economiile în timp folosind dobânda compusă.
    Cu ajutorul calculatorului nostru financiar, poți estima rapid cât vei avea în viitor în funcție de suma investită, contribuțiile lunare și randamentul anual.
  </p>

  <p className="mb-4">
    Indiferent dacă economisești într-un cont bancar, investești în acțiuni, ETF-uri, titluri de stat sau criptomonede, acest calculator te ajută să înțelegi impactul dobânzii și al timpului asupra banilor tăi.
  </p>

  <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
    Ce este dobânda compusă?
  </h2>

  <p className="mb-4">
    Dobânda compusă înseamnă că nu câștigi dobândă doar la suma inițială, ci și la dobânda acumulată în timp.
    Este unul dintre cele mai puternice mecanisme financiare pentru creșterea averii pe termen lung.
  </p>

  <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
    Ce poți calcula cu acest tool?
  </h2>

  <ul className="list-disc pl-6 space-y-2">
    <li>Valoarea finală a investiției</li>
    <li>Dobânda totală acumulată</li>
    <li>Impactul contribuțiilor lunare</li>
    <li>Diferența dintre strategii de investiții</li>
    <li>Impozite și comisioane (conform legislației din România)</li>
  </ul>
</section>
      
      {/* Main Calculator Section */}
      <main 
        ref={calculatorRef}
        className="max-w-7xl mx-auto px-4 sm:px-6 pb-24"
        data-testid="calculator-section"
      >
        {/* Calculator Grid - Bento Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Left Column - Inputs */}
          <div className="lg:col-span-4 space-y-6">
            <CalculatorInputs />
          </div>
          
          {/* Right Column - Results & Chart */}
          <div className="lg:col-span-8 space-y-6">
            <ResultsDisplay />
            <Suspense fallback={<div className="text-center">Se încarcă graficul...</div>}>
  <InvestmentChart />
</Suspense>
          </div>
        </div>
        
        {/* Strategy Comparison - Full Width */}
        <div className="mt-8">
          <StrategyComparison />
        </div>
        
        {/* Breakdown Table - Full Width */}
        <div className="mt-8">
          <BreakdownTable />
        </div>
        
        {/* Footer */}
<footer className="mt-16 pt-8 border-t border-border">
  <div className="text-center text-sm text-muted-foreground space-y-2">
    
    <p>
      Calculator dobândă compusă — Instrument informativ pentru planificarea financiară
    </p>

    <p className="text-xs">
      Acest calculator oferă estimări și nu constituie sfat financiar sau fiscal profesionist.
    </p>

    {/* Branding & Copyright */}
    <p className="text-xs mt-4">
      © {new Date().getFullYear()}{" "}
      <a 
        href="https://moneysmart.ro" 
        target="_blank" 
        rel="noopener noreferrer"
        className="hover:text-primary transition"
      >
        MoneySmart.ro
      </a>
      {" "}— Toate drepturile rezervate
    </p>

    <p className="text-xs">
      Powered by{" "}
      <a 
        href="https://darkoweb.ro" 
        target="_blank" 
        rel="noopener noreferrer"
        className="hover:text-primary transition"
      >
        DarkoWeb
      </a>
      {" "}• Optimized by{" "}
      <a 
        href="https://prowebit.ro" 
        target="_blank" 
        rel="noopener noreferrer"
        className="hover:text-primary transition"
      >
        ProWebIT
      </a>
    </p>

  </div>
</footer>
      </main>
      
      {/* Toast notifications */}
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <AppProvider>
      <CalculatorApp />
    </AppProvider>
  );
}

export default App;
